import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import {
  ArrowLeft,
  Phone,
  Building2,
  Briefcase,
  Clock,
  FileText,
  Download,
  CheckCircle,
  AlertTriangle,
  Mail,
  Send,
  Loader2
} from 'lucide-react';
import type { Employee } from '../types';

// Interface mise à jour avec le numéro de semaine et l'URL du PDF
interface WeeklySheet {
  id: string;
  week_start: string;
  week_number: number;
  total_hours: number;
  status: string;
  pdf_url: string | null;
}

export default function TeamMemberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // États
  const [member, setMember] = useState<Employee | null>(null);
  const [sheets, setSheets] = useState<WeeklySheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingSheetId, setSendingSheetId] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        // 1. Récupération du profil via RPC SECURITY DEFINER (bypass RLS)
        // Ne retourne jamais pin_hash, hourly_rate, ni comments (notes RH confidentielles)
        const { data: empData, error: empError } = await supabase
          .rpc('get_employee_public_info', { p_employee_id: id });

        if (empError) throw empError;
        const empRow = (empData as unknown as Employee[])?.[0] || null;
        setMember(empRow);

        // 2. Récupération des feuilles validées avec numéro de semaine et PDF
        const { data: sheetData, error: sheetError } = await supabase
          .from('weekly_sheets')
          .select('id, week_start, week_number, total_hours, status, pdf_url')
          .eq('employee_id', id)
          .eq('status', 'validated')
          .order('week_start', { ascending: false });

        if (sheetError) throw sheetError;
        setSheets(sheetData as WeeklySheet[]);

      } catch (error) {
        console.error("Erreur chargement profil:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Formatage propre : "Semaine 50 (du 11 déc.)"
  const formatWeekLabel = (sheet: WeeklySheet) => {
    const date = new Date(sheet.week_start);
    const dateStr = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return `Semaine ${sheet.week_number} (du ${dateStr})`;
  };

  // Implémentation réelle du téléchargement
  const handleDownload = (pdfUrl: string | null) => {
    if (!pdfUrl) {
      toast.warning("Le fichier PDF n'est pas disponible pour cette feuille.");
      return;
    }
    // Ouvre le PDF dans un nouvel onglet
    window.open(pdfUrl, '_blank');
  };

  // Envoi du PDF par email via Edge Function
  const handleSendEmail = async (sheetId: string) => {
    if (!member?.email) {
      toast.warning("Aucune adresse email renseignée pour cet employé.");
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Envoyer la feuille d'heures par email à ${member.email} ?`)) return;

    setSendingSheetId(sheetId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-pdf-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({ sheet_id: sheetId, employee_id: member.id })
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(err.error || `Erreur ${res.status}`);
      }

      toast.success(`Email envoyé à ${member.email}`);
    } catch (error) {
      console.error('Erreur envoi email:', error);
      toast.error(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setSendingSheetId(null);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Chargement du profil...</div>;
  if (!member) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Collaborateur introuvable.</div>;

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom: '40px', maxWidth: '600px', margin: '0 auto' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px', padding: '0 20px' }}>
        <button 
          onClick={() => navigate(-1)} 
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer', color:'#64748b' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Fiche Collaborateur</h1>
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* CARTE D'IDENTITÉ */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '30px 20px', textAlign: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#eff6ff', color: '#2563eb', fontSize: '1.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px', border: '4px solid #dbeafe' }}>
            {getInitials(member.full_name)}
          </div>
          <h2 style={{ margin: '0 0 5px 0', fontSize: '1.4rem', color: '#1e293b' }}>{member.full_name}</h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
            {member.is_interim ? (
              <span style={{ background: '#fffbeb', color: '#b45309', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '700', border: '1px solid #fcd34d' }}>
                INTÉRIMAIRE
              </span>
            ) : (
              <span style={{ background: '#f1f5f9', color: '#475569', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600' }}>
                SALARIÉ
              </span>
            )}
          </div>
        </div>

        {/* INFO CONTACT */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden' }}>
          
          <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ background: '#ecfdf5', padding: '10px', borderRadius: '10px', color: '#10b981' }}><Phone size={20} /></div>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>Téléphone mobile</div>
              {member.phone ? (
                <a href={`tel:${member.phone}`} style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: '600', textDecoration: 'none', borderBottom: '1px dotted #94a3b8' }}>{member.phone}</a>
              ) : <span style={{ fontSize: '1rem', color: '#cbd5e1', fontStyle: 'italic' }}>Non renseigné</span>}
            </div>
          </div>

          {member.is_interim && (
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ background: '#fff7ed', padding: '10px', borderRadius: '10px', color: '#ea580c' }}><Building2 size={20} /></div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>Agence d'Intérim</div>
                <div style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: '600' }}>{member.interim_agency || "Non renseignée"}</div>
              </div>
            </div>
          )}

          {member.email && (
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '10px', color: '#3b82f6' }}><Mail size={20} /></div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>Email</div>
                <a href={`mailto:${member.email}`} style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: '600', textDecoration: 'none', borderBottom: '1px dotted #94a3b8' }}>{member.email}</a>
              </div>
            </div>
          )}

          <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '10px', color: '#475569' }}><Briefcase size={20} /></div>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>Poste occupé</div>
              <div style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: '600', textTransform: 'capitalize' }}>{member.role.replace('_', ' ')}</div>
            </div>
          </div>
        </div>

        {/* HISTORIQUE DES FEUILLES VALIDÉES */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '15px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={18} color="#475569" />
            <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#334155' }}>Feuilles d'heures validées</h3>
            <span style={{ marginLeft: 'auto', background: '#e2e8f0', color: '#475569', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
              {sheets.length}
            </span>
          </div>

          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {sheets.length === 0 ? (
               <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                 Aucune feuille validée pour le moment.
               </div>
            ) : (
              sheets.map(sheet => (
                <div key={sheet.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                  
                  {/* Info Gauche */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ background: '#dcfce7', padding: '8px', borderRadius: '50%', color: '#15803d' }}>
                        <CheckCircle size={16} />
                    </div>
                    <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1e293b' }}>
                            {formatWeekLabel(sheet)}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', display:'flex', alignItems:'center', gap:'4px' }}>
                            <Clock size={12} /> Total : {sheet.total_hours}h
                        </div>
                    </div>
                  </div>

                  {/* Boutons Download + Envoi Email */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleDownload(sheet.pdf_url)}
                      title={sheet.pdf_url ? "Télécharger le PDF signé" : "PDF non disponible"}
                      disabled={!sheet.pdf_url}
                      style={{
                        background: 'white',
                        border: `1px solid ${sheet.pdf_url ? '#cbd5e1' : '#e2e8f0'}`,
                        borderRadius: '8px',
                        width: '36px', height: '36px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: sheet.pdf_url ? '#64748b' : '#cbd5e1',
                        cursor: sheet.pdf_url ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s'
                      }}
                    >
                      {sheet.pdf_url ? <Download size={18} /> : <AlertTriangle size={16} />}
                    </button>
                    {member?.email && sheet.pdf_url && (
                      <button
                        onClick={() => handleSendEmail(sheet.id)}
                        title={`Envoyer par email à ${member.email}`}
                        disabled={sendingSheetId === sheet.id}
                        style={{
                          background: sendingSheetId === sheet.id ? '#dbeafe' : 'white',
                          border: '1px solid #bfdbfe',
                          borderRadius: '8px',
                          width: '36px', height: '36px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#3b82f6',
                          cursor: sendingSheetId === sheet.id ? 'wait' : 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {sendingSheetId === sheet.id ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ACTION RAPIDE */}
        <button 
          onClick={() => navigate(`/timesheet/${member.id}`)}
          style={{ 
            marginTop: '10px', padding: '16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '14px', 
            fontSize: '1rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)'
          }}
        >
          <Clock size={20} />
          Accéder à la Saisie
        </button>

      </div>
    </div>
  );
}